import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CurrencyProvider } from "@/lib/currency";
import { Toaster } from "@/components/ui/sonner";
import i18n, { setLanguage } from "@/lib/i18n";
import { basenameForLocale, detectLocaleFromPath } from "@/lib/localeRoutes";
import LocaleSeo from "@/components/LocaleSeo";
import ScrollToTop from "@/components/ScrollToTop";
import { migrateLegacyKeys } from "@/lib/legacyKeyMigration";

// Iter 67fg — run the drivenest → rentalcar localStorage key migration ONCE on
// module load. Runs before any component mounts so language/currency providers
// see the migrated state on first read.
migrateLegacyKeys();
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import CarDetail from "@/pages/CarDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import BecomeVendor from "@/pages/BecomeVendor";
import { getViewAsVendor } from "@/lib/api";
import VendorDashboard from "@/pages/VendorDashboard";
import TodayOps from "@/pages/TodayOps";
import AgreementsList from "@/pages/AgreementsList";
import VendorInvoiceEditor from "@/pages/VendorInvoiceEditor";
import LegalPage from "@/pages/LegalPage";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import CookieReengageTip from "@/components/CookieReengageTip";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import VendorPoliciesPage from "@/pages/VendorPoliciesPage";
import QuoteApprovalPage from "@/pages/QuoteApprovalPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AgentDashboard from "@/pages/AgentDashboard";
import PartnerDashboard from "@/pages/PartnerDashboard";
import BookingSuccess from "@/pages/BookingSuccess";
import ReservationCheckout from "@/pages/ReservationCheckout";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RentalAgreement from "@/pages/RentalAgreement";
import BlogListPage from "@/pages/BlogListPage";
import BlogPostPage from "@/pages/BlogPostPage";
import LocationLandingPage from "@/pages/LocationLandingPage";
import ContactUs from "@/pages/ContactUs";
import VendorMicrosite from "@/pages/VendorMicrosite";
import EmbedSearchPage from "@/pages/EmbedSearch";
import WidgetBuilderPage from "@/pages/WidgetBuilder";
import AffiliateLeaderboardPage from "@/pages/AffiliateLeaderboard";
import FaqPage from "@/pages/Faq";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import AdminImpersonationBanner from "@/components/AdminImpersonationBanner";
import GlobalChatBubble from "@/components/GlobalChatBubble";
import GscMetaInjector from "@/components/GscMetaInjector";
import "@/App.css";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Iter 67ag — when admin/agent is in view-as-vendor mode, treat the vendor
  // dashboard route as accessible. The X-View-As-Vendor header on every API
  // call is what enforces server-side ownership; the route guard's job is just
  // to render the right page shell.
  if (role === "vendor" && user.role !== "vendor" && getViewAsVendor()) return children;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

// Synchronous OAuth detector — handles `#session_id=` arriving on ANY route.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function OAuthGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=") && location.pathname !== "/auth/callback") {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  // Iter 67dz — locale URL prefix. We detect the locale ONCE at module
  // load and bake it into `basename`; BrowserRouter doesn't support
  // changing basename at runtime, so language switches do a full
  // navigation via `window.location.assign` (see LanguageSelector +
  // MobileSettingsMenu). This also has the nice side-effect of forcing
  // a clean reload that fetches translated meta tags from `<head>`.
  const urlLocale = detectLocaleFromPath(window.location.pathname);
  const basename = basenameForLocale(urlLocale);
  // Keep i18n in sync with the URL so the very first paint already
  // shows the localized strings (avoids a flicker from EN → target).
  if (i18n.language !== urlLocale) {
    setLanguage(urlLocale);
  }

  return (
    <AuthProvider>
      <CurrencyProvider>
        <BrowserRouter basename={basename}>
          <ScrollToTop />
          <LocaleSeo />
          {/* Iter 67en++++ — inject GSC verification meta tag if admin has
              pasted a token in /admin → Site Settings. No-op otherwise. */}
          <GscMetaInjector />
          <OAuthGate>
            <ImpersonationBanner />
            <AdminImpersonationBanner />
            {/* Iter 67am — global cookie consent banner. Self-hides for
                returning visitors who already chose. */}
            <CookieConsentBanner />
            {/* Iter 67an — exit-intent re-engagement tooltip near the footer
                Cookies link; one-shot, only fires for the "essentials" cohort
                that left within 30s on a previous visit. */}
            <CookieReengageTip />
            {/* Iter 67do — PWA "Add to home screen" install banner.
                Self-hides if app is already installed, dismissed, or
                browser doesn't support installation. */}
            <PWAInstallBanner />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/cars" element={<Browse />} />
              <Route path="/cars/:id" element={<CarDetail />} />
              <Route path="/vendors/:vendor_id/policies" element={<VendorPoliciesPage />} />
              <Route path="/quote/:token" element={<QuoteApprovalPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/become-vendor" element={<BecomeVendor />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/blog/:category" element={<BlogListPage />} />
              <Route path="/blog/:category/:slug" element={<BlogPostPage />} />
              <Route path="/car-rental/:slug" element={<LocationLandingPage />} />
              <Route path="/booking/success" element={<BookingSuccess />} />
              <Route path="/checkout/:bookingId" element={<ReservationCheckout />} />
              <Route path="/dashboard/customer" element={<Protected role="customer"><CustomerDashboard /></Protected>} />
              <Route path="/dashboard/vendor" element={<Protected role="vendor"><VendorDashboard /></Protected>} />
              <Route path="/dashboard/today" element={<Protected><TodayOps /></Protected>} />
              <Route path="/dashboard/agreements" element={<Protected><AgreementsList /></Protected>} />
              <Route path="/vendor/invoices/:id" element={<Protected role="vendor"><VendorInvoiceEditor /></Protected>} />
              <Route path="/dashboard/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
              <Route path="/dashboard/agent" element={<Protected role="agent"><AgentDashboard /></Protected>} />
              {/* Iter 67bu — Partner dashboard for external_agent staff (hotels, agencies). */}
              <Route path="/dashboard/partner" element={<Protected><PartnerDashboard /></Protected>} />
              <Route path="/agreements/:bookingId" element={<Protected><RentalAgreement /></Protected>} />
              <Route path="/contact" element={<ContactUs />} />
              {/* Iter 67fa — per-vendor branded micro-site + iframe-embeddable widget. */}
              <Route path="/v/:slug" element={<VendorMicrosite variant="microsite" />} />
              <Route path="/vendors/:slug" element={<VendorMicrosite variant="microsite" />} />
              <Route path="/embed/v/:slug" element={<VendorMicrosite variant="embed" />} />
              {/* Iter 67eh — global search widget + public builder page. */}
              <Route path="/embed/search" element={<EmbedSearchPage />} />
              <Route path="/widget-builder" element={<WidgetBuilderPage />} />
              <Route path="/widget-builder/top-partners" element={<AffiliateLeaderboardPage />} />
              {/* Iter 67en — public FAQ powered by the chat-analytics promote button. */}
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/about" element={<LegalPage kind="about" />} />
            </Routes>
            {/* Iter 67em — global mount of the AI customer-support chat
                bubble. Self-hides on operator / embed / auth routes. */}
            <GlobalChatBubble />
          </OAuthGate>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
